// UPC-A label PDF (Get Labels → Barcodes). No React here so it can run in Node.
import { jsPDF } from 'jspdf';
import { upcaModules } from './upca';

const PT = 0.3528;                       // 1 pt in mm
const LH = (pt) => pt * PT * 1.08;

// UPC-A drawn GS1-style: first/last digit outside the bars, guard bars extended
// down past the human-readable digits. `left` is the left edge of the quiet
// zone, `X` the module width in mm.
function drawUpcA(pdf, gtin, left, top, X, barH, digitPt) {
  const bits   = upcaModules(gtin);
  const xs     = left + 9 * X;
  const digitH = digitPt * PT * 0.72;   // cap height of Helvetica digits
  const ext    = digitH + 0.6;
  const tall   = (i) => i <= 9 || (i >= 45 && i <= 49) || i >= 85;

  pdf.setFillColor(0, 0, 0);
  for (let i = 0; i < 95;) {
    if (bits[i] !== '1') { i++; continue; }
    let j = i;
    while (j < 95 && bits[j] === '1') j++;   // a run never crosses a tall/short boundary
    pdf.rect(xs + i * X, top, (j - i) * X, barH + (tall(i) ? ext : 0), 'F');
    i = j;
  }

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(digitPt);
  const y = top + barH + 0.3 + digitH;
  pdf.text(gtin[0],  xs - 1.5 * X,  y, { align: 'right' });
  for (let k = 1; k <= 5;  k++) pdf.text(gtin[k], xs + (6.5  + 7 * k) * X, y, { align: 'center' });
  for (let k = 6; k <= 10; k++) pdf.text(gtin[k], xs + (11.5 + 7 * k) * X, y, { align: 'center' });
  pdf.text(gtin[11], xs + 96.5 * X, y, { align: 'left' });
}

// One PDF, `qty` identical pages of W×H mm. Default label is 80×50 (landscape).
export function generateBarcodePDF({ name, sku, gtin, qty, W, H, showName }) {
  const orient = W >= H ? 'landscape' : 'portrait';
  const pdf    = new jsPDF({ orientation: orient, unit: 'mm', format: [W, H] });

  const M       = 3;
  const availW  = W - 2 * M;
  const X       = Math.min(0.66, availW / 113);   // 95 modules + 9-module quiet zones; ≤ 200 % magnification
  const mag     = X / 0.33;
  const digitPt = Math.min(16, Math.max(6, 8 * mag));
  const digitH  = digitPt * PT * 0.72;

  // Header: product name (auto-shrink, ≤ 2 lines) + SKU
  let y = M;
  let namePt = 10;
  let nameLines = [];
  if (showName) {
    for (; namePt >= 6; namePt -= 0.5) {
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(namePt);
      nameLines = pdf.splitTextToSize(name, availW);
      if (nameLines.length <= 2) break;
    }
    nameLines = nameLines.slice(0, 2);
    y += nameLines.length * LH(namePt) + LH(6.5) + 1;
  }

  const barH = Math.max(3, Math.min(22.85 * mag, H - M - y - (digitH + 0.6)));
  y += Math.max(0, H - M - y - (barH + digitH + 0.6)) / 2;   // center the symbol in the leftover space
  const bcLeft = (W - 113 * X) / 2;

  for (let i = 0; i < qty; i++) {
    if (i > 0) pdf.addPage([W, H], orient);
    if (showName) {
      pdf.setTextColor(0, 0, 0);
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(namePt);
      nameLines.forEach((line, k) => pdf.text(line, W / 2, M + k * LH(namePt), { align: 'center', baseline: 'top' }));
      pdf.setTextColor(110, 110, 110);
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(6.5);
      pdf.text(sku, W / 2, M + nameLines.length * LH(namePt), { align: 'center', baseline: 'top' });
      pdf.setTextColor(0, 0, 0);
    }
    drawUpcA(pdf, gtin, bcLeft, y, X, barH, digitPt);
  }
  return pdf;
}
